import React from 'react';
import { Link } from 'react-router-dom';
import { PhoneIcon, MapPinIcon, ArrowRightIcon, CheckIcon, Building2Icon, DropletIcon, ShieldCheckIcon } from 'lucide-react';
import { site, images, promises, scopeLine, credentials, serviceVisuals, serviceVisualAlt } from '../data/site';
import { counties, featuredCities } from '../data/serviceAreas';
import { services, categoryOrder, categoryBlurbs, servicesByCategory } from '../data/services';
import { TrustBand } from '../components/TrustBand';
import { SectionHeading } from '../components/SectionHeading';
import { ServiceCard } from '../components/ServiceCard';
import { ContactForm } from '../components/ContactForm';
import { CTABand } from '../components/CTABand';
import { ServiceAreaAccordion } from '../components/ServiceAreaAccordion';
const featuredSlugs = ['kitchens', 'bathrooms', 'basements', 'plumbing', 'roofing-siding-gutters', 'decks-patios', 'electrical', 'additions-new-construction'];
export function Home() {
  const featured = featuredSlugs.map(slug => services.find(s => s.slug === slug)).filter((s): s is (typeof services)[number] => Boolean(s));
  return <>
      {/* HERO */}
      <section className="relative bg-ink" data-id="element-295" data-mp="2qxkdpyt20cv2">
        <img src={images.home} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25" data-id="element-296" data-mp="2r0olt79n3sr0" />
        <div className="relative mx-auto grid max-w-7xl items-stretch gap-8 px-6 py-12 lg:grid-cols-[1.15fr_0.85fr] lg:gap-10 lg:py-14" data-id="element-297" data-mp="2hzi3qtrce9vn">
          <div className="flex flex-col justify-between" data-id="element-298" data-mp="2883zk8og4lw6">
            <div data-id="element-299" data-mp="288nh3oa6p89o">
              <p className="inline-flex items-center gap-2 rounded-sm bg-safety px-3 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-white" data-id="element-300" data-mp="2861bvvarn4um">
                Full Service Licensed &amp; Insured Builder
              </p>
              <h1 className="mt-5 font-display text-[3.25rem] font-bold uppercase leading-[0.92] tracking-tight text-white md:text-7xl" data-id="element-301" data-mp="1u8l4ohknb71t">
                Repair. <span className="text-safety" data-id="element-302" data-mp="1kjxl4rsiwd2o">Update.</span> Renovate.
              </h1>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-steel-200 md:text-xl" data-id="element-303" data-mp="1ondmg1fjf1lp">
                {scopeLine}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-steel-200" data-id="element-304" data-mp="1aovzw71dbv5d">
                <MapPinIcon className="h-4 w-4 text-safety" aria-hidden="true" data-id="element-305" data-mp="1fngyy34vw8s8" />
                <span className="font-semibold text-white" data-id="element-306" data-mp="23c06ggg40hj9">Serving</span>
                {counties.join(', ')} Counties, Michigan
              </div>

              <div className="mt-7 flex flex-col gap-3 sm:flex-row" data-id="element-307" data-mp="1tlx43pn3lr89">
                <a href={site.estimatePhone.href} className="flex items-center justify-center gap-2 rounded-sm bg-safety px-6 py-4 text-lg font-bold uppercase tracking-wide text-white transition hover:bg-safety-600" data-id="element-308" data-mp="1yki8805h0v1z">
                  <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-309" data-mp="2r23zjtvo8yw0" />
                  {site.estimatePhone.display}
                </a>
                <Link to="/contact" className="flex items-center justify-center gap-2 rounded-sm border-2 border-white px-6 py-4 text-lg font-bold uppercase tracking-wide text-white transition hover:bg-white hover:text-ink" data-id="element-310" data-mp="amqzjqg5ucdj">
                  Free Estimate
                </Link>
              </div>

              <ul className="mt-7 grid gap-2 sm:grid-cols-2" data-id="element-311" data-mp="3ssvs6f4bx3ck">
                {['Kitchens, baths and basements', 'Plumbing and electrical', 'Roofing, siding and gutters', 'Additions and new construction'].map(item => <li key={item} className="flex items-center gap-2 text-sm font-medium text-steel-200" data-id="element-312" data-mp="n8grdbzkwn2">
                    <CheckIcon className="h-4 w-4 shrink-0 text-safety" aria-hidden="true" data-id="element-313" data-mp="1c2opo9hdskb3" />
                    {item}
                  </li>)}
              </ul>
            </div>

            <div className="mt-8 border-t border-white/15 pt-6" data-id="element-314" data-mp="2ce13ml824jr9">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-safety" data-id="element-315" data-mp="2hc2onbp1mt5d">
                Every job, every season
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4" data-id="element-316" data-mp="2mbxfjf8tjiqf">
                {['Your budget', 'On time', 'Cleaned up', 'On schedule'].map(p => <div key={p} className="border-l-2 border-safety bg-white/5 px-3 py-2.5 text-sm font-semibold text-white" data-id="element-317" data-mp="2vasjekoe4gqk">
                      {p}
                    </div>)}
              </div>
              <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs font-semibold uppercase tracking-wide text-steel-300" data-id="element-318" data-mp="3a1rmjg6gid2r">
                {credentials.map(c => <li key={c} className="flex items-center gap-1.5" data-id="element-319" data-mp="12eq5yenn6dqr">
                    <ShieldCheckIcon className="h-4 w-4 text-safety" aria-hidden="true" data-id="element-320" data-mp="12cn8j9k5tic0" />
                    {c}
                  </li>)}
              </ul>
            </div>
          </div>

          <div className="lg:pl-2" data-id="element-321" data-mp="ogrvvlgct3h2">
            <ContactForm compact data-id="element-322" data-mp="ohjpiqqe4r4p" />
          </div>
        </div>
      </section>

      <TrustBand data-id="element-323" data-mp="1m264mpf8lqq2" />

      {/* SERVICE CATEGORIES */}
      <section className="mx-auto max-w-7xl px-6 py-14 md:py-20" data-id="element-324" data-mp="1h3md89hvjmqb">
        <SectionHeading eyebrow="What we do" title="One contractor, roof to basement" description="We are a full-service builder, so a single project can cross trades without you managing five different companies." data-id="element-325" data-mp="1cd60yspsi75j" />
        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-326" data-mp="24u8lo1vsj90u">
          {categoryOrder.map(cat => {
          const list = servicesByCategory(cat);
          return <div key={cat} className="flex flex-col rounded-sm border border-steel-200 bg-white p-6 shadow-card" data-id="element-327" data-mp="3ubneem84gyte">
                <h3 className="font-display text-2xl font-semibold uppercase tracking-tight text-ink" data-id="element-328" data-mp="3kkstbl6m459j">
                  {cat}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-steel-500" data-id="element-329" data-mp="3pikpwsi7cfk2">
                  {categoryBlurbs[cat]}
                </p>
                <ul className="mt-4 flex-1 space-y-2" data-id="element-330" data-mp="3aoy0xycl6vtk">
                  {list.map(s => <li key={s.slug} data-id="element-331" data-mp="3fjbqhk4ki8qb">
                      <Link to={`/services/${s.slug}`} className="flex items-center gap-2 text-sm font-medium text-ink-700 transition hover:text-safety" data-id="element-332" data-mp="31mosrf4sjejc">
                        <span className="h-1.5 w-1.5 shrink-0 bg-safety" aria-hidden="true" data-id="element-333" data-mp="zvv5s6sh3v1b" />
                        {s.name}
                      </Link>
                    </li>)}
                </ul>
              </div>;
        })}
          <div className="flex flex-col justify-between rounded-sm bg-ink p-6 text-steel-300" data-id="element-334" data-mp="19z0kl83w6ihp">
            <div data-id="element-335" data-mp="1npn9y9nriw14">
              <h3 className="font-display text-2xl font-semibold uppercase tracking-tight text-white" data-id="element-336" data-mp="1nq5f5vsekkl6">
                Not sure where it fits?
              </h3>
              <p className="mt-2 text-sm leading-relaxed" data-id="element-337" data-mp="1xo2yq5zbht7g">
                Handyman and honey-do projects, one-off repairs, or a whole
                renovation — call and describe it. If it is inside or outside
                your home, it is probably on our list.
              </p>
            </div>
            <Link to="/services" className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-safety hover:underline" data-id="element-338" data-mp="22n64jsn4g3gh">
              Browse all services
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-339" data-mp="89wtvxiuabwn" />
            </Link>
          </div>
        </div>
      </section>

      {/* PROMISE */}
      <section className="bg-ink" data-id="element-340" data-mp="ws8ta4mwgafx">
        <div className="mx-auto max-w-7xl px-6 py-14 md:py-20" data-id="element-341" data-mp="11smo2kzhpnw0">
          <SectionHeading light eyebrow="Our promise" title="Four things you can hold us to" description="These are the commitments the company has made to every customer, on every job." data-id="element-342" data-mp="11qtht6ssh5oj" />
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-343" data-mp="dw8ykm2jessn">
            {promises.map((p, i) => <div key={p.title} className="border-t-4 border-safety bg-ink-800 p-6" data-id="element-344" data-mp="iv3ftjkuw9an" data-mpi={i}>
                <p className="font-display text-4xl font-bold text-safety/40" data-id="element-345" data-mp="my8h5it7a4xp">
                  0{i + 1}
                </p>
                <h3 className="mt-2 font-display text-xl font-semibold uppercase tracking-tight text-white" data-id="element-346" data-mp="1ybatg1o9o8c2">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-steel-300" data-id="element-347" data-mp="238au1x2ckivs">
                  {p.body}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* FEATURED SERVICES */}
      <section className="mx-auto max-w-7xl px-6 py-14 md:py-20" data-id="element-348" data-mp="23o0x0jj90rlt">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-349" data-mp="23mfyyjxq6qj7">
          <SectionHeading eyebrow="Featured" title="Most requested work" description="The projects homeowners in Oakland, Livingston, Genesee and Washtenaw counties call us about most." data-id="element-350" data-mp="1zlw6vus0ed3x" />
          <Link to="/services" className="inline-flex items-center gap-2 rounded-sm border-2 border-ink px-5 py-3 text-sm font-bold uppercase tracking-wide text-ink transition hover:bg-ink hover:text-white" data-id="element-351" data-mp="1pqkmavtx6bo9">
            All services
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-352" data-mp="3f24mbcew5rkj" />
          </Link>
        </div>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-353" data-mp="3b35ki0ogiaxb">
          {featured.map(s => <ServiceCard key={s.slug} service={s} data-id="element-354" data-mp="cn0dev7nruj0" />)}
        </div>
      </section>

      {/* PLUMBING CALLOUT */}
      <section className="border-y border-steel-200 bg-white" data-id="element-355" data-mp="8pce7fyxnflb">
        <div className="mx-auto grid max-w-7xl items-center gap-8 px-6 py-12 lg:grid-cols-2" data-id="element-356" data-mp="mlg8185w9lx9">
          <div className="overflow-hidden rounded-sm border border-steel-200" data-id="element-357" data-mp="hozic9j1qzi2">
            <img src={serviceVisuals.plumbingHousehold} alt={serviceVisualAlt.plumbingHousehold} className="aspect-[16/10] w-full object-cover object-center" data-id="element-358" data-mp="3q2q09fy5t9is" />
          </div>
          <div data-id="element-359" data-mp="3phmja7c2lz7">
            <p className="inline-flex items-center gap-2 rounded-sm bg-safety-100 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-safety-600" data-id="element-360" data-mp="3pzsiyjssekb">
              <DropletIcon className="h-4 w-4" aria-hidden="true" data-id="element-361" data-mp="37bygrlkgk2qo" />
              Plumbing
            </p>
            <h2 className="mt-4 font-display text-3xl font-bold uppercase leading-tight tracking-tight text-ink md:text-4xl" data-id="element-362" data-mp="32cvjwoemt2ts">
              Leaky faucets, running toilet or frozen pipes?
            </h2>
            <p className="mt-3 text-base leading-relaxed text-steel-500" data-id="element-363" data-mp="1mncwo06pu5fh">
              Call {site.plumbingPhone.display} for emergency plumbing service,
              sewer and drain cleaning, burst pipes, flooding basements, water
              heaters, softeners and full plumbing inspections.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-id="element-364" data-mp="20mvqdnys5e89">
              <a href={site.plumbingPhone.href} className="flex items-center justify-center gap-2 rounded-sm bg-ink px-6 py-4 text-lg font-bold tracking-wide text-white transition hover:bg-ink-800" data-id="element-365" data-mp="25lp6lo5gbjc4">
                <PhoneIcon className="h-5 w-5 text-safety" aria-hidden="true" data-id="element-366" data-mp="1wmvby3bol317" />
                {site.plumbingPhone.display}
              </a>
              <Link to="/services/plumbing" className="flex items-center justify-center gap-2 rounded-sm border-2 border-ink px-6 py-4 text-base font-bold uppercase tracking-wide text-ink transition hover:bg-ink hover:text-white" data-id="element-367" data-mp="l653senp614y">
                Plumbing services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="mx-auto max-w-7xl px-6 py-14 md:py-20" data-id="element-368" data-mp="292bgcub1ostf">
        <SectionHeading eyebrow="Where we work" title="Four counties in southeast Michigan" description="Our crews cover Oakland, Livingston, Genesee and Washtenaw Counties." data-id="element-369" data-mp="2440byz3irail" />
        <div className="mt-6 flex flex-wrap gap-2" data-id="element-370" data-mp="1u5c4gu95udt5">
          {featuredCities.map(city => <span key={city} className="rounded-sm border border-steel-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700" data-id="element-371" data-mp="1q5abralekuq0">
              {city}
            </span>)}
        </div>
        <div className="mt-8" data-id="element-372" data-mp="3kj1lsb7n9glz">
          <ServiceAreaAccordion data-id="element-373" data-mp="16as9dtv7y39e" />
        </div>
        <Link to="/service-area" className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-safety hover:underline" data-id="element-374" data-mp="169zaff2i4i1t">
          Full service area detail
          <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-375" data-mp="1g13lw4syxkrw" />
        </Link>
      </section>

      {/* COMMERCIAL */}
      <section className="bg-ink-800" data-id="element-376" data-mp="1k400zljauyr3">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 py-14 lg:grid-cols-[1fr_auto] lg:items-center" data-id="element-377" data-mp="ncocpywipwoh">
          <div data-id="element-378" data-mp="sc0xme3twtof">
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-safety" data-id="element-379" data-mp="sbsn2sv6i0ge">
              <Building2Icon className="h-4 w-4" aria-hidden="true" data-id="element-380" data-mp="wb9rdiyvcrjz" />
              Commercial
            </p>
            <h2 className="mt-3 font-display text-3xl font-bold uppercase leading-tight tracking-tight text-white md:text-4xl" data-id="element-381" data-mp="119l0tsl9501o">
              We work on commercial property too
            </h2>
            <p className="mt-3 max-w-2xl text-base leading-relaxed text-steel-300" data-id="element-382" data-mp="2csxjnprfaxkd">
              Repair, updates and renovation for commercial spaces, ongoing
              commercial maintenance, lease-hold improvements, and code
              violation work to bring a property up to code.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row lg:flex-col" data-id="element-383" data-mp="2g8s0jsut0nf3">
            <Link to="/services/commercial" className="rounded-sm bg-safety px-6 py-3.5 text-center text-sm font-bold uppercase tracking-wide text-white transition hover:bg-safety-600" data-id="element-384" data-mp="2gauzx3hujswh">
              Commercial services
            </Link>
            <Link to="/services/code-violation" className="rounded-sm border-2 border-steel-400 px-6 py-3.5 text-center text-sm font-bold uppercase tracking-wide text-white transition hover:border-white" data-id="element-385" data-mp="33xtdntel0mqp">
              Code violation work
            </Link>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="mx-auto max-w-7xl px-6 py-14 md:py-20" data-id="element-386" data-mp="3rl8009g49fyo">
        <div className="grid gap-10 lg:grid-cols-2" data-id="element-387" data-mp="3rjx22q9cr2fh">
          <div data-id="element-388" data-mp="e3yfu2qqlxn">
            <SectionHeading eyebrow="Get started" title="Request your free estimate" description="Call the estimate line or send the form and we will get back to you with next steps." data-id="element-389" data-mp="ewyydb3wf7c" />
            <dl className="mt-8 space-y-5 text-sm" data-id="element-390" data-mp="38vi9vznatid3">
              <div data-id="element-391" data-mp="38tnoylc11q90">
                <dt className="font-display text-lg font-semibold uppercase tracking-wide text-ink" data-id="element-392" data-mp="38t5iywzkbxnw">
                  Estimates &amp; Plumbing
                </dt>
                <dd data-id="element-393" data-mp="2yydk7pu8v4rj">
                  <a href={site.estimatePhone.href} className="font-display text-3xl font-bold text-safety hover:underline" data-id="element-394" data-mp="2yyvo98n5ena0">
                    {site.estimatePhone.display}
                  </a>
                </dd>
              </div>
              <div data-id="element-395" data-mp="2uunyr98v93aq">
                <dt className="font-display text-lg font-semibold uppercase tracking-wide text-ink" data-id="element-396" data-mp="2uxa6b8vrbisy">
                  E-mail
                </dt>
                <dd data-id="element-397" data-mp="vnhb1tkiwid6">
                  <a href={site.emailHref} className="break-all text-base font-medium text-steel-500 hover:text-safety" data-id="element-398" data-mp="vmz70armczup">
                    {site.email}
                  </a>
                </dd>
              </div>
              <div data-id="element-399" data-mp="1jecvg5mngbae">
                <dt className="font-display text-lg font-semibold uppercase tracking-wide text-ink" data-id="element-400" data-mp="1jdl1t0cm4nmr">
                  Mailing address
                </dt>
                <dd className="text-base text-steel-500" data-id="element-401" data-mp="1ad6fpyrtr4i7">
                  {site.address.company}, {site.address.line1},{' '}
                  {site.address.city}, {site.address.state} {site.address.zip}
                </dd>
              </div>
            </dl>
          </div>
          <ContactForm data-id="element-402" data-mp="3v1az8jye33bz" />
        </div>
      </section>

      <CTABand data-id="element-403" data-mp="3q3rk7htvamiz" />
    </>;
}